import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const OrganizationCampaignsAdmin = () => {
  const { token } = useAuth();
  const [pendingCampaigns, setPendingCampaigns] = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pendingRes, activeRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/admin/org-campaigns/pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/admin/org-campaigns/active`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (pendingRes.ok) setPendingCampaigns(await pendingRes.json());
      if (activeRes.ok) setActiveCampaigns(await activeRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, notes = '') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/org-campaigns/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status, adminNotes: notes })
      });

      if (response.ok) {
        fetchData(); // Refresh data
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const viewDonations = async (campaignId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/org-campaigns/${campaignId}/donations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const donationData = await response.json();
        setDonations(donationData);
        setSelectedCampaign(campaignId);
      }
    } catch (error) {
      console.error('Error fetching donations:', error);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-2xl font-bold">Organization Campaigns Management</h1>
      
      {/* Pending Campaigns Section */}
      <section>
        <h2 className="text-xl font-semibold mb-4">Pending Campaigns ({pendingCampaigns.length})</h2>
        <div className="grid gap-4">
          {pendingCampaigns.map(campaign => (
            <div key={campaign.id} className="bg-white p-4 rounded-lg shadow">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{campaign.product_name}</h3>
                  <p>Organization: {campaign.organization_name}</p>
                  <p>Contact: {campaign.first_name} {campaign.last_name} ({campaign.email})</p>
                  <p>Requested: {campaign.quantity} units (${campaign.total_price})</p>
                  <p>Reason: {campaign.reason}</p>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => updateStatus(campaign.id, 'approved')}
                    className="bg-green-500 text-white px-3 py-1 rounded"
                  >
                    Approve
                  </button>
                  <button 
                    onClick={() => {
                      const notes = prompt('Enter rejection reason:');
                      if (notes) updateStatus(campaign.id, 'rejected', notes);
                    }}
                    className="bg-red-500 text-white px-3 py-1 rounded"
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Active Campaigns Section */}
      <section>
        <h2 className="text-xl font-semibold mb-4">Active Campaigns ({activeCampaigns.length})</h2>
        <div className="grid gap-4">
          {activeCampaigns.map(campaign => (
            <div key={campaign.id} className="bg-white p-4 rounded-lg shadow">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{campaign.product_name}</h3>
                  <p>Organization: {campaign.organization_name}</p>
                  <p>Requested: {campaign.quantity} units (${campaign.total_price})</p>
                  <p>Progress: {campaign.units_fulfilled || 0}/{campaign.quantity} units fulfilled</p>
                  <p>Raised: ${campaign.amount_raised || 0} of ${campaign.total_price}</p>
                </div>
                <div>
                  <button 
                    onClick={() => viewDonations(campaign.id)}
                    className="bg-blue-500 text-white px-3 py-1 rounded"
                  >
                    View Donations
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Donations Modal */}
      {selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-11/12 max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Donations for Campaign #{selectedCampaign}</h2>
              <button onClick={() => setSelectedCampaign(null)} className="text-red-500">Close</button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {donations.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-left">Donor</th>
                      <th className="text-left">Units Funded</th>
                      <th className="text-left">Amount</th>
                      <th className="text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donations.map(donation => (
                      <tr key={donation.id}>
                        <td>{donation.first_name} {donation.last_name} ({donation.email})</td>
                        <td>{donation.units_funded}</td>
                        <td>${donation.amount}</td>
                        <td>{new Date(donation.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No donations yet for this campaign.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationCampaignsAdmin;